import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { adaAlert, adaSwal } from '../utils/adaSwal';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { additionalServicesBasedOnLocation, commonPackages } from '../data/servicesData';
import { formatSelectedServices } from '../utils/formatSelectedServices';
import { buildApiUrl } from '../utils/apiUrl';
import { checkClientVersion } from '../utils/clientVersion';
import { useAuth } from '../contexts/AuthContext';
import { TIMEOUTS } from '../config/timeouts';
import {
	fetchMyDraft,
	saveMyDraft,
	deleteMyDraft,
} from '../api/formDraftApi';

// subsections
import Footer from '../sections/pageSections/footer';
import FormContent from './subsections/formContent';

const log = (...args) => {
	if (process.env.NODE_ENV === 'development') {
		console.log('[SalesForm]', ...args);
	}
};

function normalizeHttpUrl(value) {
	const trimmed = String(value || '').trim();
	if (!trimmed) {
		return '';
	}
	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed;
	}
	if (trimmed.includes('.') && !/\s/.test(trimmed)) {
		return `https://${trimmed}`;
	}
	return trimmed;
}

function formatSubmitErrorBody(resData) {
	if (typeof resData === 'string') {
		return resData;
	}
	if (!resData || typeof resData !== 'object') {
		return String(resData);
	}
	const base = resData.error || resData.message || '';
	const details = Array.isArray(resData.details)
		? resData.details
				.map((d) =>
					d && typeof d === 'object'
						? `${d.field || '?'}: ${d.message || ''}`
						: String(d)
				)
				.filter(Boolean)
				.join('; ')
		: '';
	if (base && details) {
		return `${base} (${details})`;
	}
	return base || details || JSON.stringify(resData);
}

/** Verdi når «Send inn» fra navigasjonsdialog er fullført (navigasjon skjer i onSubmit). */
const LEAVE_DIALOG_SUBMIT_HANDLED = '__leave_dialog_submit_handled__';

function getLeaveDialogBodyHtml() {
	return `
		<p style="margin:0 0 1.25rem;text-align:left;font-size:0.95rem;line-height:1.45;">
			Hva vil du gjøre med påbegynt bestilling?
		</p>
		<div class="swal-leave-form-actions">
			<button type="button" class="swal2-styled swal2-confirm" data-leave="save" style="margin:0;background:green;color:white;">
				Lagre utkast og forlat
			</button>
			<button type="button" class="swal2-styled" data-leave="stay" style="margin:0;background:#1a1a1a;color:#fff;">
				Bli i skjemaet
			</button>
			<button type="button" class="swal2-styled swal2-confirm" data-leave="submit" style="margin:0;background:#005379;color:white;">
				Send inn (med validering)
			</button>
			<button type="button" class="swal2-styled swal2-cancel" data-leave="quit" style="margin:0;background:red;color:white;">
				Avslutt og slett utkast
			</button>
		</div>
	`;
}

function getLeaveDialogSubmittingHtml() {
	return `
		<p style="margin:0 0 0.75rem;text-align:center;font-size:0.95rem;line-height:1.45;">
			Vent litt mens bestillingen sendes inn.
		</p>
		<div class="swal-leave-form-submitting-wrap">
			<input type="button" class="swal-leave-form-submit-like-section-seven" value="Sender inn…" disabled />
		</div>
	`;
}

/**
 * Navigasjonsblokker: fire tydelige knapper.
 * «Send inn» bytter til én «Sender inn…»-knapp (som skjemaets submit) og kjører validering + onSubmit her.
 * Blokkeren resettes ikke her (unngår useEffect-cleanup mens dialogen er åpen); parent kaller reset ved fullført innsending.
 */
function promptLeaveFormDialog(getActions) {
	return new Promise((resolve) => {
		let settled = false;
		let submitInFlight = false;

		const settle = (value) => {
			if (settled) {
				return;
			}
			settled = true;
			resolve(value);
		};

		const closeAndSettle = (value) => {
			settle(value);
			Swal.close();
		};

		const restoreLeaveDialog = (root) => {
			submitInFlight = false;
			root.innerHTML = getLeaveDialogBodyHtml();
			Swal.update({
				allowOutsideClick: true,
				icon: 'question',
				title: 'Forlate skjema?',
			});
		};

		const showSubmitting = (root) => {
			submitInFlight = true;
			root.innerHTML = getLeaveDialogSubmittingHtml();
			Swal.update({
				allowOutsideClick: false,
				icon: 'info',
				title: 'Sender inn',
			});
		};

		void adaSwal({
			width: 800,
			icon: 'question',
			title: 'Forlate skjema?',
			html: getLeaveDialogBodyHtml(),
			animation: false,
			showConfirmButton: false,
			showCancelButton: false,
			allowOutsideClick: true,
			focusConfirm: false,
			customClass: { popup: 'swal-leave-form-dialog' },
			didOpen: () => {
				const root = Swal.getHtmlContainer();
				if (!root) {
					return;
				}
				root.addEventListener('click', async (e) => {
					const btn = e.target.closest('button[data-leave]');
					if (!btn || !root.contains(btn)) {
						return;
					}
					e.preventDefault();
					const v = btn.getAttribute('data-leave');
					if (v === 'save') {
						closeAndSettle('save');
						return;
					}
					if (v === 'stay') {
						closeAndSettle('stay');
						return;
					}
					if (v === 'quit') {
						closeAndSettle('quit');
						return;
					}
					if (v !== 'submit' || submitInFlight) {
						return;
					}

					showSubmitting(root);

					const { handleSubmit: hs, onSubmit: os } = getActions() || {};
					if (!hs || !os) {
						restoreLeaveDialog(root);
						return;
					}

					try {
						await hs(
							async (data) => {
								await os(data);
								settle(LEAVE_DIALOG_SUBMIT_HANDLED);
							},
							async () => {
								await adaSwal({
									icon: 'warning',
									title: 'Sjekk skjemaet',
									text: 'Rett feilene som er markert, og prøv «Send inn» igjen.',
								});
								restoreLeaveDialog(root);
							}
						)();
					} catch {
						restoreLeaveDialog(root);
					}
				});
			},
		}).then(() => {
			if (settled) {
				return;
			}
			settled = true;
			resolve('stay');
		});
	});
}

const SalesForm = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const methods = useForm({
		defaultValues: {
			// Unngå undefined → definert verdi (controlled/uncontrolled-advarsel) for checkboxes m.m.
			hybridHome: false,
		},
	});
	const { handleSubmit, setValue, getValues, watch, control, register } = methods;
	const [selectedServices, setSelectedServices] = useState({});
	const [userDepartment, setUserDepartment] = useState('');
	const [sending, setSending] = useState(false);
	const { user, devMailMode, isLoggedIn } = useAuth();
	const sendingLock = useRef(false);
	const submissionIdRef = useRef(null);

	const autosaveEnabledRef = useRef(false);
	const lastAutosavedJsonRef = useRef('');
	const autosaveDebounceRef = useRef(null);
	const autosaveUiClearRef = useRef(null);
	const suppressNavigationBlockRef = useRef(false);
	const autosaveAbortRef = useRef(null);
	/** Bare én fane skal autosave — unngår last-writer-wins mellom faner (A.17). */
	const autosaveLeaderRef = useRef(true);
	const tabIdRef = useRef(
		typeof crypto !== 'undefined' && crypto.randomUUID
			? crypto.randomUUID()
			: `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`
	);
	const draftInitDoneRef = useRef(false);
	/** Når det ikke finnes server-utkast (`lastAutosavedJsonRef` tom): sammenlign mot dette etter init. */
	const initialNavigationBaselineJsonRef = useRef(null);
	const blockerActionsRef = useRef({});
	const [autosaveUi, setAutosaveUi] = useState('');

	const buildDraftPayload = useCallback(
		(data) => {
			const draftData = {
				...data,
				additionalServices: selectedServices,
				servicesColumnList: formatSelectedServices(selectedServices),
			};
			delete draftData.companyProfile;
			delete draftData.submissionId;
			return draftData;
		},
		[selectedServices]
	);

	/** Brukes i async/utkast-effekt slik at serialisering ikke fanger gammel `selectedServices`. */
	const buildDraftPayloadRef = useRef(buildDraftPayload);
	buildDraftPayloadRef.current = buildDraftPayload;

	const applyDraftServerData = useCallback(
		(savedData) => {
			if (!savedData || typeof savedData !== 'object') {
				return;
			}
			Object.keys(savedData).forEach((key) => {
				if (key === 'additionalServices') {
					return;
				}
				setValue(key, savedData[key]);
			});
			if (
				savedData.additionalServices &&
				typeof savedData.additionalServices === 'object'
			) {
				setSelectedServices(savedData.additionalServices);
			}
			if (savedData.selectedLocation) {
				setUserDepartment(savedData.selectedLocation);
			}
		},
		[setValue]
	);

	useEffect(() => {
		autosaveAbortRef.current = new AbortController();
		return () => {
			autosaveAbortRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (!user?.department) {
			return;
		}
		setUserDepartment(user.department);
		// Ikke overskriv lokasjon som allerede er satt (f.eks. fra utkast)
		if (!getValues('selectedLocation')) {
			setValue('selectedLocation', user.department, { shouldDirty: false });
		}
	}, [user, setValue, getValues]);

	// Innlogget utkast på forsiden (/): GET /draft/me + valgfri Swal (hopp over rett etter innlogging).
	useEffect(() => {
		if (!isLoggedIn) {
			draftInitDoneRef.current = false;
			autosaveEnabledRef.current = false;
			initialNavigationBaselineJsonRef.current = null;
			return undefined;
		}
		if (draftInitDoneRef.current) {
			return undefined;
		}

		let cancelled = false;
		autosaveEnabledRef.current = false;

		const finishEnableAutosave = () => {
			if (!cancelled) {
				autosaveEnabledRef.current = true;
			}
			draftInitDoneRef.current = true;
			window.setTimeout(() => {
				if (cancelled) {
					return;
				}
				try {
					const json = JSON.stringify(
						buildDraftPayloadRef.current(getValues())
					);
					if (lastAutosavedJsonRef.current !== '') {
						initialNavigationBaselineJsonRef.current = null;
					} else {
						initialNavigationBaselineJsonRef.current = json;
					}
				} catch {
					initialNavigationBaselineJsonRef.current = null;
				}
			}, 0);
		};

		/** Etter `applyDraftServerData`: vent til React har committet `selectedServices` før vi leser payload. */
		const scheduleSyncLastSavedAndEnableAutosave = () => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (cancelled) {
						return;
					}
					try {
						lastAutosavedJsonRef.current = JSON.stringify(
							buildDraftPayloadRef.current(getValues())
						);
					} catch {
						/* ignore */
					}
					finishEnableAutosave();
				});
			});
		};

		const run = async () => {
			const skipWelcome = Boolean(location.state?.skipDraftWelcomeSwal);
			if (skipWelcome) {
				navigate('.', { replace: true, state: {} });
			}

			try {
				const data = await fetchMyDraft({
					signal: autosaveAbortRef.current?.signal,
				});
				if (cancelled) return;

				const draft = data?.draft;
				if (!draft?.data) {
					lastAutosavedJsonRef.current = '';
					finishEnableAutosave();
					return;
				}

				if (skipWelcome) {
					applyDraftServerData(draft.data);
					scheduleSyncLastSavedAndEnableAutosave();
					return;
				}

				const savedAt = draft.savedAt
					? new Date(draft.savedAt).toLocaleString('nb-NO')
					: '';
				const sw = await adaSwal({
					icon: 'question',
					title: 'Lagret utkast',
					html: `<p>Vi fant et påbegynt skjema${
						savedAt ? ` (lagret ${savedAt})` : ''
					}.</p><p>Vil du fortsette der du slapp, eller starte på nytt?</p>`,
					showDenyButton: true,
					confirmButtonText: 'Fortsett på utkast',
					denyButtonText: 'Slett utkast og start blankt',
				});

				if (cancelled) return;

				if (sw.isDenied) {
					try {
						await deleteMyDraft({
							signal: autosaveAbortRef.current?.signal,
						});
					} catch (e) {
						console.error(e);
						await adaSwal({
							icon: 'error',
							title: 'Kunne ikke slette utkast',
							text: 'Prøv igjen eller kontakt support.',
						});
					}
					lastAutosavedJsonRef.current = '';
					finishEnableAutosave();
					return;
				}

				if (sw.isConfirmed) {
					applyDraftServerData(draft.data);
					scheduleSyncLastSavedAndEnableAutosave();
				} else {
					lastAutosavedJsonRef.current = '';
					finishEnableAutosave();
				}
			} catch (e) {
				if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
					return;
				}
				console.error('[SalesForm] draft/me ved mount:', e);
				finishEnableAutosave();
			}
		};

		void run();

		return () => {
			cancelled = true;
		};
		// Ikke `buildDraftPayload` i deps: effekten skal bare kjøre init én gang; payload leses via `buildDraftPayloadRef`.
	}, [isLoggedIn, navigate, location.state, applyDraftServerData, getValues]);

	const formData = watch();

	// Én fane leder autosave (BroadcastChannel).
	useEffect(() => {
		if (!isLoggedIn || typeof BroadcastChannel === 'undefined') {
			autosaveLeaderRef.current = true;
			return undefined;
		}
		const channel = new BroadcastChannel('ada-form-autosave');
		const claim = () => {
			autosaveLeaderRef.current = true;
			channel.postMessage({ type: 'claim', tabId: tabIdRef.current });
		};
		const onMessage = (event) => {
			const msg = event.data;
			if (!msg || msg.type !== 'claim' || msg.tabId === tabIdRef.current) {
				return;
			}
			autosaveLeaderRef.current = false;
		};
		channel.addEventListener('message', onMessage);
		const onFocus = () => claim();
		window.addEventListener('focus', onFocus);
		claim();
		return () => {
			channel.removeEventListener('message', onMessage);
			channel.close();
			window.removeEventListener('focus', onFocus);
		};
	}, [isLoggedIn]);

	// Debouncet autosave (kun innlogget forsiden, ikke recover-URL).
	useEffect(() => {
		if (!isLoggedIn) {
			return undefined;
		}
		const sub = watch(() => {
			if (!autosaveEnabledRef.current || !autosaveLeaderRef.current) {
				return;
			}
			clearTimeout(autosaveDebounceRef.current);
			autosaveDebounceRef.current = setTimeout(async () => {
				if (!autosaveLeaderRef.current) {
					return;
				}
				let snapshot;
				try {
					snapshot = getValues();
				} catch {
					return;
				}
				const payload = buildDraftPayload(snapshot);
				let json;
				try {
					json = JSON.stringify(payload);
				} catch {
					return;
				}
				if (json === lastAutosavedJsonRef.current) {
					return;
				}
				try {
					setAutosaveUi('Lagrer…');
					await saveMyDraft(payload, {
						signal: autosaveAbortRef.current?.signal,
					});
					lastAutosavedJsonRef.current = json;
					setAutosaveUi('Lagret');
					clearTimeout(autosaveUiClearRef.current);
					autosaveUiClearRef.current = setTimeout(() => {
						setAutosaveUi((u) => (u === 'Lagret' ? '' : u));
					}, 2000);
				} catch (e) {
					if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
						return;
					}
					console.error('[SalesForm] autosave:', e);
					setAutosaveUi('Ikke lagret');
				}
			}, TIMEOUTS.formAutosaveDebounceMs);
		});
		return () => {
			sub.unsubscribe();
			clearTimeout(autosaveDebounceRef.current);
			clearTimeout(autosaveUiClearRef.current);
		};
	}, [watch, isLoggedIn, getValues, buildDraftPayload]);

	log('Selected Services State:', selectedServices);

	const storeError = (errorData) => {
		const errors = localStorage.getItem('frontendErrors');
		const parsedErrors = errors ? JSON.parse(errors) : [];
		parsedErrors.push(errorData);
		localStorage.setItem('frontendErrors', JSON.stringify(parsedErrors));
	};

	const logErrorToBackend = (errorData) => {
		fetch(buildApiUrl('log/frontend-error'), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(errorData),
		}).catch((err) =>
			console.error('Error logging frontend error to backend:', err)
		);
	};

	const saveDraftAfterSubmitError = async (data) => {
		try {
			const payload = buildDraftPayload(data);
			await saveMyDraft(payload, {
				signal: autosaveAbortRef.current?.signal,
			});
			const json = JSON.stringify(payload);
			lastAutosavedJsonRef.current = json;
			initialNavigationBaselineJsonRef.current = null;
			await adaSwal({
				icon: 'info',
				title: 'Skjema lagret',
				html: '<p>Noe gikk galt ved innsending, men utkastet er lagret på server.</p><p>Du kan prøve å sende inn igjen senere.</p>',
				confirmButtonText: 'OK',
			});
		} catch (error) {
			console.error('[SalesForm] Klarte ikke å lagre utkast etter innsendingsfeil:', error);
			await adaSwal({
				icon: 'warning',
				title: 'Kunne ikke lagre utkast',
				text: 'Sjekk nettverk og at du er innlogget, og prøv igjen.',
				confirmButtonText: 'OK',
			});
		}
	};

	const onSubmit = async (data) => {
		if (sendingLock.current) {
			return;
		}
		if (!(await checkClientVersion())) {
			return;
		}
		sendingLock.current = true;
		if (!submissionIdRef.current) {
			submissionIdRef.current =
				typeof crypto !== 'undefined' && crypto.randomUUID
					? crypto.randomUUID()
					: `${Date.now()}-${Math.random().toString(16).slice(2)}`;
		}
		setSending(true);

		log('Final selected services state:', selectedServices);
		const findServiceOrSubPackageById = (serviceId) => {
			for (const location of Object.values(
				additionalServicesBasedOnLocation
			)) {
				for (const packages of Object.values(location)) {
					const foundService = packages.find(
						(service) => service.id === serviceId
					);
					if (foundService) return foundService;
				}
			}

			for (const packages of Object.values(commonPackages)) {
				const foundService = packages.find(
					(service) => service.id === serviceId
				);
				if (foundService) return foundService;
				for (const service of packages.filter(
					(service) => service.subPackages
				)) {
					const foundSubPackage = service.subPackages.find(
						(sub) => sub.id === serviceId
					);
					if (foundSubPackage) return foundSubPackage;
				}
			}

			return null;
		};

		const selectedServicesDetails = Object.entries(selectedServices)
			.filter(
				([_, details]) =>
					details.isChecked ||
					Object.values(details.subPackages || {}).some((v) => v)
			)
			.map(([serviceId, details]) => {
				const service = findServiceOrSubPackageById(serviceId);
				log('Processing service:', serviceId, details);
				if (!service) {
					console.error('Service not found:', serviceId);
					return { name: '', subPackages: [] };
				}

				const subPackageNames = Object.entries(
					details.subPackages || {}
				)
					.filter(([_, isChecked]) => isChecked)
					.map(([subPackageId, _]) => {
						const subPackage =
							service.subPackages &&
							service.subPackages.find(
								(sub) => sub.id === subPackageId
							);
						if (!subPackage) {
							console.error(
								'Sub-package not found:',
								subPackageId,
								'in service',
								serviceId
							);
						}
						return subPackage ? subPackage.name : undefined;
					})
					.filter((name) => name);

				return { name: service.name, subPackages: subPackageNames };
			});

		log('Selected services details:', selectedServicesDetails);


		data.servicesColumnList = formatSelectedServices(selectedServices);

		const submissionData = {
			...data,
			additionalServices: selectedServices,
			submissionId: submissionIdRef.current,
			landingpage: normalizeHttpUrl(data.landingpage),
		};

		// Include frontend-only dev mail flags for backend handling.
		// Backend must still validate role/permissions before honoring these.
		if (devMailMode) {
			submissionData.devMailMode = true;
			submissionData.devMailTo = user?.email || data.ordererEmail || '';
		}

		log('Submission Data:', submissionData);

		try {
			const formData = new FormData();

			formData.append('formData', JSON.stringify(submissionData));

			const files = getValues('companyProfile');
			if (files && files.length) {
				files.forEach((file) => formData.append('companyProfile', file));
			}

			// axios: withCredentials via App interceptors; 401 TOKEN_EXPIRED can refresh + retry
			await axios.post(buildApiUrl('form/submit-form'), formData);
			submissionIdRef.current = null;

			// Lukk åpen Swal (f.eks. «Forlate skjema?») før ruteskifte — ellers kan backdrop/body-klasser
			// henge igjen når SalesForm unmonteres og blokkere klikk på resten av appen.
			if (Swal.isVisible()) {
				Swal.close();
			}

			suppressNavigationBlockRef.current = true;
			navigate('/orders');
			window.setTimeout(() => {
				suppressNavigationBlockRef.current = false;
				if (!Swal.isVisible()) {
					document
						.querySelectorAll('.swal2-container')
						.forEach((el) => el.remove());
					document.body.classList.remove(
						'swal2-shown',
						'swal2-height-auto',
						'swal2-iosfix'
					);
					document.documentElement.classList.remove(
						'swal2-shown',
						'swal2-height-auto'
					);
				}
			}, 0);
			sendingLock.current = false;
			setSending(false);
		} catch (error) {
			console.error('Form submission error:', error);

			let errorData;
			if (axios.isAxiosError(error) && error.response) {
				const { status, data: resData } = error.response;
				const bodySnippet = formatSubmitErrorBody(resData);
				errorData = {
					message: `Skjemainnsending feilet (${status}): ${bodySnippet}`,
					source: 'Form submission',
					lineno: 0,
					colno: 0,
					error: bodySnippet,
					status,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			} else if (axios.isAxiosError(error)) {
				errorData = {
					message: `Skjemainnsending: ${error.message}`,
					source: 'Form submission',
					lineno: 0,
					colno: 0,
					error: error.message,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			} else {
				errorData = {
					message: error.message,
					source: window.location.href,
					lineno: error.lineNumber || 0,
					colno: error.columnNumber || 0,
					error: error.stack || null,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				};
			}

			storeError(errorData);
			logErrorToBackend(errorData);

			sendingLock.current = false;
			setSending(false);

			await saveDraftAfterSubmitError(data);

			throw error;
		}
	};

	blockerActionsRef.current = { getValues, buildDraftPayload, handleSubmit, onSubmit };

	const shouldPromptNavigationAway = Boolean(isLoggedIn);

	const hasUnsavedNavigationChanges = useCallback(() => {
		if (!autosaveEnabledRef.current) {
			return false;
		}
		let json;
		try {
			json = JSON.stringify(buildDraftPayload(getValues()));
		} catch {
			return true;
		}
		const saved = lastAutosavedJsonRef.current;
		if (saved !== '') {
			return json !== saved;
		}
		const baseline = initialNavigationBaselineJsonRef.current;
		if (baseline === null) {
			return false;
		}
		return json !== baseline;
	}, [getValues, buildDraftPayload]);

	const hasUnsavedNavigationChangesRef = useRef(hasUnsavedNavigationChanges);
	hasUnsavedNavigationChangesRef.current = hasUnsavedNavigationChanges;

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		if (!shouldPromptNavigationAway) {
			return false;
		}
		if (suppressNavigationBlockRef.current) {
			return false;
		}
		if (currentLocation.pathname === nextLocation.pathname) {
			return false;
		}
		return hasUnsavedNavigationChangesRef.current();
	});

	useEffect(() => {
		if (blocker.state !== 'blocked') {
			return undefined;
		}
		let cancelled = false;

		const run = async () => {
			const v = await promptLeaveFormDialog(
				() => blockerActionsRef.current
			);

			if (v === LEAVE_DIALOG_SUBMIT_HANDLED) {
				blocker.reset();
				return;
			}

			if (v === 'stay') {
				blocker.reset();
				return;
			}

			if (cancelled) {
				return;
			}

			const { getValues: gv, buildDraftPayload: bdp } = blockerActionsRef.current;

			if (v === 'save') {
				try {
					setAutosaveUi('Lagrer…');
					await saveMyDraft(bdp(gv()), {
						signal: autosaveAbortRef.current?.signal,
					});
					lastAutosavedJsonRef.current = JSON.stringify(bdp(gv()));
					setAutosaveUi('Lagret');
				} catch (e) {
					console.error(e);
					await adaSwal({
						icon: 'error',
						title: 'Kunne ikke lagre utkast',
					});
					blocker.reset();
					return;
				}
				suppressNavigationBlockRef.current = true;
				blocker.proceed();
				window.setTimeout(() => {
					suppressNavigationBlockRef.current = false;
				}, 0);
				return;
			}

			if (v === 'quit') {
				try {
					await deleteMyDraft({
						signal: autosaveAbortRef.current?.signal,
					});
					lastAutosavedJsonRef.current = '';
				} catch (e) {
					console.error(e);
					await adaSwal({
						icon: 'error',
						title: 'Kunne ikke slette utkast',
					});
					blocker.reset();
					return;
				}
				suppressNavigationBlockRef.current = true;
				blocker.proceed();
				window.setTimeout(() => {
					suppressNavigationBlockRef.current = false;
				}, 0);
				return;
			}

		};

		void run();

		return () => {
			cancelled = true;
		};
	}, [blocker.state, blocker]);

	useEffect(() => {
		if (!isLoggedIn) {
			return undefined;
		}
		const onBeforeUnload = (e) => {
			if (!hasUnsavedNavigationChangesRef.current()) {
				return;
			}
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [isLoggedIn]);

	const handleChange = (name, value) => {
		setValue(name, value);
		log('formData (utkast)', formData);
	};

	return (
		<FormProvider {...methods}>
			<form
				className='w-2/3 mx-auto pt-12'
				onSubmit={handleSubmit(onSubmit, async (errors) => {
					if (errors.packageChoice) {
						await adaAlert(
							'Velg en bestillingspakke før du går videre.'
						);
						return;
					}
					await adaAlert('Fyll ut alle påkrevde felt.');
				})}
			>
				{isLoggedIn && autosaveUi ? (
					<p
						className='mb-2 text-right text-xs text-slate-600'
						aria-live='polite'
					>
						{autosaveUi}
					</p>
				) : null}
				<FormContent
					control={control}
					register={register}
					handleChange={handleChange}
					formData={formData}
					setValue={setValue}
					getValues={getValues}
					setSelectedServices={setSelectedServices}
					preselectedLocation={userDepartment}
					sending={sending}
				/>
				<input type='hidden' {...register('selectedLocation')} />
				<Footer />
			</form>
		</FormProvider>
	);
};

export default SalesForm;
